// FLAG:docuAiEnhancements — „Antwort melden": a knowledge-base gap reported from the receipt chat.
//
// Deliberately its OWN component and its OWN target, not a second caller of
// ReportAnalysisProblemButton: that one reports what the ANALYSIS got wrong about a document
// (target 'docuai', needs a minioId, forwarded to DocuAI's worklist). This reports what the
// ANSWER got wrong — a missing or incorrect passage in the knowledge base (target 'ragchat', no
// document required, stays in the DMS reports worklist where the RAG-KB gap tooling picks it up
// via diag_dms_kb_gap_suggest). Two different problems, two different worklists; collapsing them
// would put chat feedback in front of the people triaging document analyses.
//
// It sits per ANSWER rather than once per chat: the reported exchange is exactly this question and
// this answer, and a single global button would force the user to describe which turn they meant.
import React, { useRef } from 'react'
import { makeStyles } from '@mui/styles'
import { IconButton, Tooltip } from '@mui/material'
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt'
import lang from '@/lang'
import { ButtonSquare, CancelButton } from '@/modules/base/components/buttons'
// Deliberately NOT via the '@/modules/base/components/inputs' barrel — it drags in
// react-tag-input, whose untransformed ESM breaks isolated jest suites that import this
// component (the same trap report-analysis-problem-button documents).
import TextareaInput from '@/modules/base/components/inputs/textarea-input/textarea-input'
import { Title } from '@/modules/base/components/texts'
import { useOpenModal } from '@/modules/_helpers/openModal'
import { toast } from '@/modules/base/components/toast/toastConfig'
import FileService from '@/modules/file/fileService'

const chatLang = lang.documents.ragChat

const useStyles = makeStyles(() => ({
  modalWrapper: { padding: 22 },
  button: { width: 145 },
  title: { textAlign: 'left' },
}))

type ReportChatAnswerButtonProps = {
  /** The question that produced the reported answer. */
  question?: string
  /** The answer being reported. */
  answer: string
  /** The open document, when the chat sits next to one — binds this report for triage. */
  minioId?: string
}

const ReportChatAnswerButton = ({ question, answer, minioId }: ReportChatAnswerButtonProps) => {
  const classes = useStyles()
  const openModal = useOpenModal()
  // Held in a ref, not state: the modal's action buttons are built once and would otherwise close
  // over the empty initial value (same reason as the analysis-problem dialog).
  const reportTextRef = useRef('')

  const onSubmit = (close: () => void) => {
    const description = reportTextRef.current.trim()
    if (!description) return
    FileService.reportProblem({ target: 'ragchat', description, question, answer, minioId })
      .then(() => {
        toast.showToast({ type: 'success', message: chatLang.reportAnswerSuccess })
        close()
      })
      .catch((error) => {
        // The dialog stays open and the text survives in the ref, so "try again" is honest advice
        // for a transient failure. FileService maps 429 (anti-spam cap) and network errors.
        toast.showToast({ type: 'error', message: FileService.getReportProblemErrorMessage(error) })
      })
  }

  const handleClick = () => {
    reportTextRef.current = ''
    openModal((close: () => void) => ({
      component: 'ModalSmall',
      children: <div className={classes.modalWrapper}>
        <Title className={classes.title} position='center'>{chatLang.reportAnswerTitle}</Title>
        <TextareaInput
          useFormik={false}
          enableAiFeatures={false}
          name='reportChatAnswer'
          rows={5}
          placeholder={chatLang.reportAnswerPlaceholder}
          onChange={(value: string) => { reportTextRef.current = value }}
        />
      </div>,
      props: {
        onBackdropClick: close,
        showCloseIcon: false,
        actions: [
          <CancelButton type='cancel' label={lang.cancelButtonLabel} onClick={close} className={classes.button} dataQsId='reportChatAnswer-cancel' />,
          <ButtonSquare className={classes.button} onClick={() => { onSubmit(close) }} label={chatLang.reportAnswerSubmit} dataQsId='reportChatAnswer-submit' />,
        ],
      },
    }))
  }

  return (
    <Tooltip title={chatLang.reportAnswer}>
      <IconButton size="small" aria-label={chatLang.reportAnswer} onClick={handleClick} data-qs-id='reportChatAnswer'>
        <ThumbDownOffAltIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  )
}

export default ReportChatAnswerButton
